import { toast } from 'react-toastify';
import React, { useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Link } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import "./signUp.css";
import { ROUTES, MESSAGE } from '../../../constant';
import BaseNavbar from '../../commanComponents/navbar/baseNavbar';
import { signUpService } from "../../../modules/services";
import { saveJsonData, saveData } from "../../../utils/localStorage";
import TextInputField from '../../commanComponents/textInputField/textInputField';
import PrimaryButton from '../../commanComponents/primaryButton/primaryButton';
import TurnstileWidget from '../../commanComponents/turnstileWidget/turnstileWidget';
import { Row, Col } from "react-bootstrap";
import InputDropdown from '../../commanComponents/inputDropdown/inputDropdown';


const SignUp = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [captchaResetKey, setCaptchaResetKey] = useState(0);

    const genderItems = [
        {label: "Male", value: "MALE"},
        {label: "Female", value: "FEMALE"},
        {label: "Others", value: "OTHERS"},
    ]

    const signUpSchema = z.object({
        first_name: z.string().min(1, { message: "First Name is required" }),
        last_name: z.string().min(1, { message: "Last Name is required" }),
        email: z.string()
            .min(1, { message: MESSAGE.THIS_REQUEIRED_FIELD.replace("{0}", "Email") })
            .email(MESSAGE.FIELD_INVALID),
        password: z.string().min(6, { message: "Password must be at least 6 characters" }),
        confirm_password: z.string().min(1, { message: "Confirm Password is required" }),
        gender: z.string().min(1, { message: "Gender is required" }),
        dob: z.string().min(1, { message: "Date of Birth is required" }),
        role: z.string().default("USER")
    }).refine((data) => data.password === data.confirm_password, {
        message: "Passwords do not match",
        path: ["confirm_password"],
    });

    const { register, control, handleSubmit, setError, formState: { errors } } = useForm({
        resolver: zodResolver(signUpSchema)
    });

    const handleSignUp = (data) => {
        if (!captchaToken) {
            toast.error("Please complete the verification and try again.");
            return;
        }
        console.log(data)
        setIsLoading(true);
        signUpService({ ...data, captcha_token: captchaToken }, {}).then(apiResponse => {
            toast.success(apiResponse.data.message[0], {
                position: toast.POSITION.TOP_RIGHT,
            });
            saveJsonData("user", apiResponse.data.data);
            saveData("access", apiResponse.data.data.token.access);
            saveData("refresh", apiResponse.data.data.token.refresh);
            setIsLoading(false);

            window.location.pathname = ROUTES.BOOKS_SELLING_HOME_PAGE;
        }).catch(error => {
            setIsLoading(false);
            const errorKey = error?.response?.data?.error;
            if (errorKey && errorKey !== "captcha_token") {
                setError(errorKey, { type: "custom", message: error?.response?.data?.message[0] })
            } else {
                toast.error(error?.response?.data?.message?.[0] || error.message || "Unable to create your account.", {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        }).finally(() => {
            setCaptchaToken(null);
            setCaptchaResetKey((key) => key + 1);
        });
    }

    return (
        <>
            <BaseNavbar activeItem="SIGN_UP" />
            <div className='sign-up-main-div'>
                <div className='sign-up-form-div'>
                    <Form onSubmit={handleSubmit(handleSignUp)}>
                        <Form.Label className='heading'>
                            SIGN UP
                        </Form.Label>
                        <Form.Label className='sub-heading'>
                            Welcome, to FIRE Robotics Lab(FRL)!
                            <br />Let the FIRE begin!
                        </Form.Label>

                        {/* First + Last Name */}
                        <Row>
                            <Col xs={12} md={6}>
                                <TextInputField
                                    placeholder="First Name"
                                    fieldType="text"
                                    errorMessage={errors.first_name?.message}
                                    inputKey="first_name"
                                    formRegister={register}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <TextInputField
                                    placeholder="Last Name"
                                    fieldType="text"
                                    errorMessage={errors.last_name?.message}
                                    inputKey="last_name"
                                    formRegister={register}
                                />
                            </Col>
                        </Row>

                        {/* Email + DOB */}
                        <Row>
                            <Col xs={12} md={6}>
                                <TextInputField
                                    placeholder="Email"
                                    fieldType="email"
                                    errorMessage={errors.email?.message}
                                    inputKey="email"
                                    formRegister={register}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <TextInputField
                                placeholder="Date of Birth"
                                fieldType="date"
                                errorMessage={errors.dob?.message}
                                inputKey="dob"
                                formRegister={register}
                                />
                            </Col>
                        </Row>

                        {/* Password + Confirm Password */}
                        <Row>
                            <Col xs={12} md={6}>
                                <TextInputField
                                placeholder="Password"
                                fieldType="password"
                                errorMessage={errors.password?.message}
                                inputKey="password"
                                formRegister={register}
                                />
                            </Col>
                            <Col xs={12} md={6}>
                                <TextInputField
                                placeholder="Confirm Password"
                                fieldType="password"
                                errorMessage={errors.confirm_password?.message}
                                inputKey="confirm_password"
                                formRegister={register}
                                />
                            </Col>
                        </Row>

                        {/* Gender */}
                        <Row>
                            <Col xs={12} md={6}>
                                <InputDropdown
                                    inputKey="gender"
                                    itemList={genderItems}
                                    placeholder="Gender"
                                    formRegister={control}
                                    errorMessage={errors.gender?.message}
                                />
                            </Col>
                        </Row>

                        <br />

                        <TurnstileWidget key={captchaResetKey} action="sign_up" onToken={setCaptchaToken} />
                        <div className='submit-button-div'>
                            <PrimaryButton
                                text="SIGN UP"
                                type={"submit"}
                                isLoading={isLoading}
                                onlyDisabled={isLoading || !captchaToken}
                                customStyle={{ height: "40px" }}
                            />
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
}

export default SignUp;
